import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useOnboarding } from './ImportJobContext';
import { STARTER_MENUS, STARTER_MENU_CHOICES, hasStarterMenu, starterRows, salonBusinessType } from './starterMenus';
import { createStarterServices, createSampleClient, createPracticeBooking } from './starterSetup';
import { setDemoTeam, tradeForBusinessType } from './demoTeam';
import { IconTick } from './icons';
import './StarterScreen.css';

/**
 * First-run Steps A and B (ONBOARDING-FIRST-RUN-PLAN.md).
 *
 *  step="start"  the welcome fork: ready-made setup (primary) or bring data.
 *  step="menu"   the starter menu for the signup business type, every row
 *                ticked and editable inline, then one button that creates the
 *                services, a sample client and one practice booking.
 */

const pick = (user) => (user && user.userInfo && user.userInfo.hairdresser) || {};

const IconWand = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M11 3.5l1.6 4.2 4.2 1.6-4.2 1.6L11 15.1 9.4 10.9 5.2 9.3l4.2-1.6z" />
    <path d="M17.5 14.5l.8 2 2 .8-2 .8-.8 2-.8-2-2-.8 2-.8z" />
  </svg>
);
const IconUpload = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 16V4" /><path d="m7.5 8.5 4.5-4.5 4.5 4.5" /><path d="M4 16v2.5A2.5 2.5 0 0 0 6.5 21h11a2.5 2.5 0 0 0 2.5-2.5V16" />
  </svg>
);
const IconArrow = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 6l6 6-6 6" />
  </svg>
);

/**
 * Step A, the welcome fork. This is the highest friction point in the whole
 * product: the owner has paid, landed, and now has to believe the setup is
 * short. So the screen shows the outcome rather than describing it, with the
 * first services of their own trade already priced, and says out loud that
 * nothing here is permanent.
 */
export function StartScreen({ salonName, onStarter, onImport, onSkip }) {
  const user = useSelector((s) => s.user);
  const h = pick(user);
  const currencySymbol = h.currencySymbol || '£';
  const type = salonBusinessType(user);
  const known = hasStarterMenu(type);
  const label = known ? STARTER_MENUS[type].label : 'your kind of business';
  const rows = known ? starterRows(type, { withPrices: currencySymbol === '£' }) : [];
  const peek = rows.slice(0, 3);

  return (
    <div className="ob-screen obst-start">
      <div className="ob-eyebrow-row">
        <span className="ob-eyebrow">Welcome</span>
        <span className="obst-time">About a minute</span>
      </div>
      <h2 className="ob-h1">Let's get {salonName || 'you'} ready.</h2>
      <p className="ob-sub">Two ways in. Both leave you with a diary you can book into today, and nothing you pick here is permanent.</p>

      <div className="ob-screen-scroll">
        <button type="button" className="obst-opt obst-opt--primary" onClick={onStarter}>
          <span className="obst-opt-top">
            <span className="obst-opt-icon"><IconWand /></span>
            <span className="obst-opt-head">
              <span className="obst-fork-tag">Recommended</span>
              <b>Start with a ready-made setup</b>
              <span className="obst-opt-sub">Typical services for {label}, ready to rename and reprice. Plus a practice client and booking, so you can try the diary on.</span>
            </span>
            <span className="obst-opt-go" aria-hidden="true"><IconArrow /></span>
          </span>

          {!!peek.length && (
            <span className="obst-peek">
              {peek.map((r, i) => (
                <span className="obst-peek-row" style={{ animationDelay: `${120 + i * 70}ms` }} key={r.key}>
                  <span className="obst-peek-tick" aria-hidden="true"><IconTick size={12} /></span>
                  <span className="obst-peek-name">{r.name}</span>
                  <span className="obst-peek-meta">{r.time} min{r.price ? ` · ${currencySymbol}${r.price}` : ''}</span>
                </span>
              ))}
              {rows.length > peek.length && (
                <span className="obst-peek-more" style={{ animationDelay: `${120 + peek.length * 70}ms` }}>
                  and {rows.length - peek.length} more, all yours to change
                </span>
              )}
            </span>
          )}
        </button>

        <button type="button" className="obst-opt" onClick={onImport}>
          <span className="obst-opt-top">
            <span className="obst-opt-icon obst-opt-icon--quiet"><IconUpload /></span>
            <span className="obst-opt-head">
              <b>Bring my data from another system</b>
              <span className="obst-opt-sub">Drop in exports, spreadsheets, a price list or a screenshot. We read them and tell you what we found.</span>
            </span>
            <span className="obst-opt-go" aria-hidden="true"><IconArrow /></span>
          </span>
        </button>

        <ul className="obst-reassure">
          <li><span aria-hidden="true"><IconTick size={11} /></span>Change anything later</li>
          <li><span aria-hidden="true"><IconTick size={11} /></span>Nothing is sent to clients</li>
          <li><span aria-hidden="true"><IconTick size={11} /></span>Skip any step</li>
        </ul>
      </div>

      <div className="obst-start-foot">
        <button type="button" className="obst-skip" onClick={onSkip}>I'll explore on my own</button>
      </div>
    </div>
  );
}

export function StarterMenuScreen({ onBack, onDone }) {
  const user = useSelector((s) => s.user);
  const { onboarding, patchOnboarding } = useOnboarding();
  const h = pick(user);
  const currencySymbol = h.currencySymbol || '£';
  const withPrices = currencySymbol === '£';
  const signupType = salonBusinessType(user);
  const [type, setType] = useState(hasStarterMenu(signupType) ? signupType : '');
  const [rows, setRows] = useState(() => (hasStarterMenu(signupType) ? starterRows(signupType, { withPrices }) : []));
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [err, setErr] = useState('');

  const chooseType = (t) => { setType(t); setRows(starterRows(t, { withPrices })); };
  const update = (key, patch) => setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const onCount = rows.filter((r) => r.on && r.name.trim()).length;

  const groups = useMemo(() => {
    const out = [];
    rows.forEach((r) => {
      let g = out.find((x) => x.name === r.category);
      if (!g) { g = { name: r.category, rows: [] }; out.push(g); }
      g.rows.push(r);
    });
    return out;
  }, [rows]);

  const submit = async () => {
    if (busy || !onCount) return;
    setBusy(true); setErr('');
    try {
      const { created } = await createStarterServices(rows, (i, n) => setProgress({ i, n }));
      setProgress(null);
      const prior = (onboarding && onboarding.checklist && onboarding.checklist.practice) || {};
      let practice = { ...prior };
      let note = '';
      try {
        const clientId = prior.clientId || await createSampleClient();
        practice.clientId = clientId;
        if (!prior.bookingId) {
          const names = rows.filter((r) => r.on).map((r) => r.name);
          const b = await createPracticeBooking({ clientId, preferNames: names });
          practice = { ...practice, bookingId: b.bookingId, staffId: b.staffId, date: b.date };
        }
      } catch (e) {
        note = 'Your services are in. The practice booking could not be added, so make one with the + button.';
      }
      // Two example columns beside theirs, and the walk round the diary that
      // explains them, before anyone is asked to do anything.
      setDemoTeam(true, tradeForBusinessType(signupType));
      await patchOnboarding({ status: 'in_progress', checklist: { starter: 'done', practice, tour: 'pending' } });
      onDone({ created, practice, note });
    } catch (e) {
      setErr(e.message || 'Could not add your services. Please try again.');
      setProgress(null);
      setBusy(false);
    }
  };

  const typeLabel = type ? STARTER_MENUS[type].label : '';

  return (
    <div className="ob-screen">
      <div className="ob-eyebrow-row"><span className="ob-eyebrow">Your menu</span></div>
      <h2 className="ob-h1">{type ? `Typical services for ${typeLabel}.` : 'Which menu is closest?'}</h2>
      <p className="ob-sub">
        {type
          ? 'Untick anything you do not offer. Tap a name, time or price to change it. A service is just those three things.'
          : 'Pick the nearest one to start from. You can rename, reprice or remove anything.'}
      </p>

      <div className="ob-screen-scroll">
        {!hasStarterMenu(signupType) && (
          <div className="obst-types" role="radiogroup" aria-label="Starter menu">
            {STARTER_MENU_CHOICES.map((c) => (
              <button key={c.id} type="button" role="radio" aria-checked={type === c.id} className={`obst-type${type === c.id ? ' is-on' : ''}`} onClick={() => chooseType(c.id)} disabled={busy}>{c.label}</button>
            ))}
          </div>
        )}

        {groups.map((g) => (
          <section className="obst-group" key={g.name}>
            <div className="obst-group-name">{g.name}</div>
            <div className="obst-rows">
              {g.rows.map((r) => (
                <div className={`obst-row${r.on ? '' : ' is-off'}`} key={r.key}>
                  <button type="button" className={`obst-tick${r.on ? ' is-on' : ''}`} onClick={() => update(r.key, { on: !r.on })} aria-pressed={r.on} aria-label={r.on ? `Remove ${r.name}` : `Add ${r.name}`} disabled={busy}>
                    <IconTick size={11} />
                  </button>
                  <input className="obst-name" value={r.name} onChange={(e) => { const v = e.target.value; update(r.key, { name: v }); }} disabled={busy || !r.on} aria-label="Service name" />
                  <label className="obst-num obst-num--time">
                    <input type="number" min="5" step="5" inputMode="numeric" value={r.time} onChange={(e) => { const v = e.target.value; update(r.key, { time: v }); }} disabled={busy || !r.on} aria-label={`${r.name} minutes`} />
                    <span>min</span>
                  </label>
                  <label className="obst-num obst-num--price">
                    <span>{currencySymbol}</span>
                    <input type="number" min="0" step="1" inputMode="decimal" placeholder="0" value={r.price} onChange={(e) => { const v = e.target.value; update(r.key, { price: v }); }} disabled={busy || !r.on} aria-label={`${r.name} price`} />
                  </label>
                </div>
              ))}
            </div>
          </section>
        ))}

        {type && <p className="ob-live-note">Everyone on your team can do these, and clients can book them online. Change that per service in Catalogue.</p>}
        {err && <div className="ob-error" role="alert">{err}</div>}
      </div>

      <div className="obst-foot">
        <button type="button" className="ob-btn ob-btn--secondary" onClick={onBack} disabled={busy}>Back</button>
        <button type="button" className="ob-btn ob-btn--primary" onClick={submit} disabled={busy || !onCount}>
          {busy
            ? (progress && progress.n ? `Adding ${Math.min(progress.i + 1, progress.n)} of ${progress.n}…` : 'Setting up…')
            : `Add ${onCount} ${onCount === 1 ? 'service' : 'services'}`}
        </button>
      </div>
    </div>
  );
}
